import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResourceService } from 'libraries/service/apiResource/apiResource.service';
import { IIsActive } from 'libraries/model/extenal-service/externalService';
import { ICheckOutput } from './ICheck';
import { EResource } from "libraries/enums/EResource";
import { PropertyResourceService } from "libraries/service/resources/hotel/property-resource.service";
import { tap } from "rxjs/operators";
import { IProperty } from "libraries/service/resources/hotel/ISearchResponse";

@Injectable()
export class ExternalServiceResource {
    constructor(
        private readonly apiResourceService: ApiResourceService,
        private readonly propertyResourceService: PropertyResourceService
    ) {
    }

    public create<TExternalServiceRequest, TExternalServiceResponse>(body: TExternalServiceRequest): Observable<TExternalServiceResponse> {
        return this.apiResourceService.postOne(
            this.apiResourceService.getApiUrl(EResource.watabi, EResource.externalService, 'create'),
            body
        );
    }

    public getOne<TExternalServiceResponse>(externalServiceId: number): Observable<TExternalServiceResponse> {
        return this.apiResourceService.getOne(
            this.apiResourceService.getApiUrl(EResource.watabi, EResource.externalService, externalServiceId)
        )
            .pipe(
                tap((externalService: TExternalServiceResponse): void => {
                    this.cacheHotelProperties(externalService);
                })
            );
    }

    public getAll<TExternalServiceResponse>(bookingId: number | string): Observable<TExternalServiceResponse[]> {
        return this.apiResourceService.get(
            this.apiResourceService.getApiUrl(EResource.watabi, EResource.bookings, bookingId, EResource.externalService)
        )
            .pipe(
                tap((externalServices: TExternalServiceResponse[]): void => {
                    externalServices.forEach((externalService: TExternalServiceResponse): void => this.cacheHotelProperties(externalService));
                })
            );
    }

    public update<TExternalServiceRequest, TExternalServiceResponse>(externalServiceId: number, body: TExternalServiceRequest):
        Observable<TExternalServiceResponse> {
        return this.apiResourceService.patchOne(
            this.apiResourceService.getApiUrl(EResource.watabi, EResource.externalService, externalServiceId),
            body
        )
            .pipe(
                tap((externalService: TExternalServiceResponse): void => {
                    this.cacheHotelProperties(externalService);
                })
            );
    }

    public check<TExternalServiceResponse>(bookingId: number): Observable<ICheckOutput[]> {
        return this.apiResourceService.post<{}, ICheckOutput>(
            this.apiResourceService.getApiUrl(EResource.watabi, EResource.bookings, bookingId, EResource.externalService, 'check'),
            {}
        );
    }

    public deactivate<TExternalServiceResponse>(externalServiceId: number): Observable<TExternalServiceResponse> {
        return this.apiResourceService.patchOne<IIsActive, TExternalServiceResponse>(
            this.apiResourceService.getApiUrl(EResource.watabi, EResource.externalService, externalServiceId),
            {isActive: false}
        );
    }

    public deactivateSeveral<TExternalServiceResponse>(externalServiceIdArray: number[]): Observable<TExternalServiceResponse[]> {
        return this.apiResourceService.patch<IIsActive, TExternalServiceResponse>(
            this.apiResourceService.getApiUrl(EResource.watabi, EResource.externalService, externalServiceIdArray.join(',')),
            {isActive: false}
        );
    }

    private cacheHotelProperties<TExternalServiceResponse>(externalService: TExternalServiceResponse): void {
        // @ts-ignore
        if (externalService.service?.properties) {
            // @ts-ignore
            Object.values(externalService.service.properties).forEach((property: IProperty): void => {
                this.propertyResourceService.setPropertyInCache(property);
            });
        }
    }
}
